import { Box, Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { MdAdd, MdEdit, MdOutlineAltRoute } from 'react-icons/md';

import { TableColumn } from '@app/components/common/table';
import { Txt } from '@app/components/common';
import { Employee, Level, StructureNode } from '@app/api/models/structure';

import { OrgStructureNoData, OrgStructureViewNode } from './components/node';
import { ChainItem } from './types';

export const useTreeColumns = ({ chain, levels, onItemClick, onMoveUpClick, onEdit, onAdd, onResubordinate, canChange }: {
  chain: ChainItem[];
  levels: Level[];
  onItemClick: (item: StructureNode) => void;
  onMoveUpClick: (item: StructureNode) => void;
  onEdit: (item: StructureNode) => void;
  onAdd: (item: StructureNode) => void;
  onResubordinate: (item: StructureNode) => void;
  canChange: boolean;
}): TableColumn<StructureNode>[] => {
  return [
    {
      name: 'territory',
      label: 'Уровень / Территория',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_, { rowData, totalRows, rowIndex }) => {
          if (rowData.id === -1) {
            const parentLevelIndex = levels.findIndex(({ id }) => id === chain[chain.length - 1].item.level.id); 
            return <OrgStructureNoData index={parentLevelIndex + 1} />;
          }
          if (!chain.length) {
            return <OrgStructureViewNode
              data={rowData}
              isLastLevel={false}
              index={0}
              parentIndex={0}
              onItemClick={onItemClick}
            />;
          }
          const index = chain.findIndex(({ item }) => item.id === rowData.id);
          const isLastLevel = levels[levels.length - 1].id === rowData.level.id;
          const levelIndex = levels.findIndex(({ id }) => id === rowData.level.id);
          if (index !== -1) {
            const parentLevelIndex = index > 0 ? levels.findIndex(({ id }) => id === chain[index - 1].item.level.id) : -1; 
            return <OrgStructureViewNode
              data={rowData}
              isLastLevel={false}
              index={levelIndex}
              parentIndex={parentLevelIndex}
              onItemClick={onMoveUpClick}
              linkUp={index > 0 ? 1 : undefined}
              linkDown
            />;
          }
          const parentLevelIndex = levels.findIndex(({ id }) => id === chain[chain.length - 1].item.level.id);  

          return <OrgStructureViewNode
            data={rowData}
            index={levelIndex}
            parentIndex={parentLevelIndex}
            isLastLevel={isLastLevel}
            onItemClick={onItemClick}
            linkUp={1}
            linkMore={totalRows > rowIndex + 1} 
          />;
        },
      },
    },
    {
      name: 'employee',
      label: 'Сотрудник / Вакансия',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (employee: Employee | null, { rowData }) => {
          if (rowData.id === -1) {
            return null;
          }
          if (!employee) {
            return <Txt color='red.500'>Вакансия</Txt>;
          }
          return <Box >
            <Txt color='brand.500'>{employee.name}</Txt>
            <Box display='flex'>
              <Txt color='gray.500'>{rowData.position.name}</Txt>
            </Box>
          </Box>;
        },
      },  
    },
    {
      name: 'route_number',
      label: 'Номер маршрута',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (route_number: EntityBase | null, { rowData }) => {
          if (rowData.id === -1) {
            return null;
          }
          if (!route_number) {
            return <Txt color='red.500'></Txt>;
          }
          return <Box >
            <Txt color='brand.500'>{route_number.name}</Txt> 
          </Box>;
        },
      },  
    },
    ...(canChange ? [{
      name: 'id',
      label: 'Действия',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (id, { rowData }) => {
          if (rowData.id === -1) {
            return null;
          }
          const isTopLevel = levels[0].id === rowData.level.id;
          const isLastLevel = levels[levels.length - 1].id === rowData.level.id;
          return (
            <Flex gap={2}>
              <Tooltip label='Редактировать'>
                <IconButton name="edit" onClick={() => onEdit(rowData)} aria-label="edit" size='sm'>
                  <MdEdit />
                </IconButton>
              </Tooltip>
              {!isTopLevel && <Tooltip label='Переподчинить'>
                <IconButton name="edit" colorScheme='brand' onClick={() => onResubordinate(rowData)} aria-label="edit" size='sm'>
                  <MdOutlineAltRoute />
                </IconButton>
              </Tooltip>}
              {!isLastLevel && <Tooltip label='Добавить дочернее подразделение'>
                <IconButton ml={1} name="add" colorScheme='green' onClick={() => onAdd(rowData)} aria-label="add" size='sm'> 
                  <MdAdd />
                </IconButton>
              </Tooltip>}
            </Flex>
          );
        },
      },  
    }] as TableColumn<StructureNode>[] : []),
  ];
};